import { copyTextToClipboard, type NetworkType, truncateString, useIntl, WithSkeleton } from '@capsicum/common';
import { Divider, Image, ListCard, NetworkIcon, Skeleton, SkeletonItem } from '@capsicum/common-web';
import { ChilizIcon } from '@capsicum/common-web/icons';
import {
	RECEIVE_NETWORK_LABELS,
	RECEIVE_NETWORK_SHORTCODES,
	type ReceiveNetworks,
	type WalletRecipientItem
} from '@capsicum/features/wallet/shared';
import { type StaticImageData } from 'next/image';
import { Fragment } from 'react';
import styled, { useTheme } from 'styled-components';

export type WithdrawTransactionCardProps = {
	isExternalToken?: boolean;
	partner?: {
		id?: string;
		logo?: StaticImageData | string;
	};
	recipient?: WalletRecipientItem;
	withdrawInfo?: {
		amount?: string;
		chzAmount?: string;
		convertedPrice?: string;
		formattedAmount?: string;
		isCHZWithdraw?: boolean;
	};
};

const WithdrawTransactionCardSkeleton = () => {
	const rowContainers = new Array(5).fill(0).map((_, index) => (
		<Fragment key={index}>
			{index > 0 && <S.NetworkDivider orientation="horizontal" />}
			<S.RowContainer>
				<S.SkeletonRowLeftItem />
				<S.SkeletonRowRightItem />
			</S.RowContainer>
		</Fragment>
	));

	return (
		<Skeleton>
			<S.Container>
				<S.Content>{rowContainers}</S.Content>
			</S.Container>
		</Skeleton>
	);
};

const WithdrawTransactionCardComponent = ({
	isExternalToken,
	partner,
	recipient,
	withdrawInfo = {}
}: WithdrawTransactionCardProps) => {
	const { $t } = useIntl();
	const { colors } = useTheme();

	const texts = {
		addressLabel: $t('wallet.withdraw.confirmation.addressLabel'),
		amountLabel: withdrawInfo?.isCHZWithdraw
			? $t('wallet.withdraw.confirmation.totalAmountLabel')
			: $t('wallet.withdraw.confirmation.amountLabel'),
		exchangeLabel: $t('wallet.withdraw.confirmation.exchangeLabel'),
		networkLabel: $t('wallet.withdraw.confirmation.networkLabel'),
		recipientLabel: $t('wallet.withdraw.confirmation.recipientLabel')
	};

	return (
		<S.Container>
			<S.Content>
				<S.RowContainer>
					<S.TransactionLabel>{texts.amountLabel}</S.TransactionLabel>
					<S.AmountContainer>
						<S.LogoRow>
							{withdrawInfo?.isCHZWithdraw ? (
								<ChilizIcon color={colors.icons.main.inverse} height={24} width={24} />
							) : (
								<Image alt={`${partner?.id}`} height={24} src={partner?.logo as string} width={24} />
							)}
							{withdrawInfo?.isCHZWithdraw ? withdrawInfo?.chzAmount : withdrawInfo?.formattedAmount}
						</S.LogoRow>
						{!isExternalToken && `≈${withdrawInfo?.convertedPrice}`}
					</S.AmountContainer>
				</S.RowContainer>
				<S.NetworkDivider orientation="horizontal" />
				<S.RowContainer>
					<S.TransactionLabel>{texts.recipientLabel}</S.TransactionLabel>
					<S.TransactionText>{recipient?.label}</S.TransactionText>
				</S.RowContainer>
				<S.NetworkDivider orientation="horizontal" />
				<S.RowContainer>
					<S.TransactionLabel>{texts.exchangeLabel}</S.TransactionLabel>
					<S.TransactionText>{recipient?.exchange || '--'}</S.TransactionText>
				</S.RowContainer>
				<S.NetworkDivider orientation="horizontal" />
				<S.RowContainer>
					<S.TransactionLabel>{texts.networkLabel}</S.TransactionLabel>
					<S.Network>
						<NetworkIcon network={recipient?.networkType as NetworkType} size="default" variant="default" />
						<S.TransactionText>
							{recipient?.networkType &&
								`${RECEIVE_NETWORK_LABELS[recipient?.networkType as ReceiveNetworks]} (${
									RECEIVE_NETWORK_SHORTCODES[recipient?.networkType as ReceiveNetworks]
								})`}
						</S.TransactionText>
					</S.Network>
				</S.RowContainer>
				<S.NetworkDivider orientation="horizontal" />
				<S.RowContainer>
					<S.TransactionLabel>{texts.addressLabel}</S.TransactionLabel>
					<S.Address
						onClick={() => {
							copyTextToClipboard(recipient?.blockchainAddress || '');
						}}
					>
						{truncateString(recipient?.blockchainAddress || '', 8, 8)}
					</S.Address>
				</S.RowContainer>
			</S.Content>
		</S.Container>
	);
};

export const WithdrawTransactionCard = WithSkeleton(WithdrawTransactionCardComponent, WithdrawTransactionCardSkeleton);

namespace S {
	export const Container = styled(ListCard)`
		height: auto;
	`;

	export const Content = styled(ListCard.Content)`
		padding: ${({ theme }) => theme.spacings.small};
		overflow: hidden;
		display: inline-block;
	`;
	export const TransactionLabel = styled.span`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
	export const TransactionText = styled.span`
		${({ theme }) => theme.fonts.text.text1};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;
	export const RowContainer = styled.div`
		display: flex;
		justify-content: space-between;
	`;

	export const SkeletonRowLeftItem = styled(SkeletonItem)`
		height: 18px;
		width: 200px;
	`;
	export const SkeletonRowRightItem = styled(SkeletonItem)`
		height: 18px;
		width: 80px;
	`;

	export const AmountContainer = styled.div`
		margin: 0;
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.subtle};
		text-align: right;
	`;
	export const LogoRow = styled.div`
		margin: 0;
		display: flex;
		align-items: center;
		flex-direction: row;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const NetworkDivider = styled(Divider)`
		margin: ${({ theme }) => theme.spacings.xsmall} 0px;
	`;

	export const Network = styled.span`
		${({ theme }) => theme.fonts.text.text1};
		display: flex;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		justify-content: center;
		align-items: center;
	`;
	export const Address = styled.span`
		cursor: pointer;
		${({ theme }) => theme.fonts.text.text1};
		color: ${({ theme }) => theme.colors.borders.brand.enabled};
	`;
}
